import * as React from 'react';

import {DEFAULT_BUCKET_NAME, FILTER_MODE_BUCKET_NAME} from 'nutshell-core/constants';
import * as Schema from 'nutshell-core/schema';
import type {FilterObject, EntityListType} from 'nutshell-core/types';

import {FilterBucketSection} from './filter-bucket-section';

import styles from './filter-bucket.module.css';

type Props = {
    title?: string;
    fields: Schema.FieldProperties[];
    allowNegation: boolean;
    openFilters: string[];
    primaryFilters?: string[];
    activeFilters: FilterObject[];
    shouldShowSecondaryFilters: boolean;
    entityType: EntityListType;
    onOpenSection: (filterName: string) => any;
    onCloseSection: (filterName: string) => any;
    onSubmitFilter: (filterObj: FilterObject, field: Schema.FieldProperties) => void;
    onRemoveFilter: (filterKey: string) => void;
    shownColumns?: string[];
    updateShownColumns?: (arg1: string[], arg2: boolean) => void;
    onShowAllClick: () => any;
};

/**
 * This is used to show a group of filters inside a filter drawer.  If a title is provided,
 * a header will be shown at the top of the group, for example for custom fields.
 *
 * @param {string} [title]              - If provided, will be shown at the top of the group
 * @param {Object[]} fields             - Array of schema fields
 * @param {string[]} openFilters        - List of filter names which should be open,
 *                                        can be a super-set of active filters,
 *                                        as a section can be open without being filled in or applied
 * @param {string[]} primaryFilters     - List of filter names to display,
 *                                        any others will be hidden behind a "show all" link
 * @param {Object[]} activeFilters      - List of actively-applied filter objects
 * @param {string} entityType           - The type of entity being filtered ('leads', 'contacts', or 'accounts')
 * @param {Function} onOpenSection      - Callback used to open a filter section
 * @param {Function} onCloseSection     - Callback used to close a filter section and unapply the filter
 * @param {Function} onSubmitFilter     - Callback to apply a filter
 * @param {Function} onRemoveFilter     - Callback to remove a filter without closing the section
 * @return {Object}                     - React component for group of filters
 */
export class FilterBucket extends React.PureComponent<Props> {
    static defaultProps = {
        onShowAllClick: () => {},
    };

    render() {
        const {primaryFilters} = this.props;
        let primaryFields = this.props.fields;
        // @ts-expect-error
        let secondaryFields = [];

        if (primaryFilters && this.props.title === DEFAULT_BUCKET_NAME) {
            primaryFields = this.props.fields.filter((field) =>
                primaryFilters.includes(field.name)
            );
            secondaryFields = this.props.fields.filter(
                (field) => !primaryFilters.includes(field.name)
            );
        }

        const bucketSection = (
            <div>
                <FilterBucketSection
                    {...this.props}
                    fields={primaryFields}
                    shownColumns={this.props.shownColumns}
                    updateShownColumns={this.props.updateShownColumns}
                />
                {secondaryFields.length && !this.props.shouldShowSecondaryFilters ? (
                    <div className={styles['show-all-link']}>
                        <a href='#' onClick={this.handleShowSecondaryFilters}>
                            Show all
                        </a>
                    </div>
                ) : undefined}
                {secondaryFields.length && this.props.shouldShowSecondaryFilters ? (
                    <FilterBucketSection
                        {...this.props}
                        // @ts-expect-error
                        fields={secondaryFields}
                        shownColumns={this.props.shownColumns}
                        updateShownColumns={this.props.updateShownColumns}
                    />
                ) : undefined}
            </div>
        );

        if (
            this.props.title &&
            ![DEFAULT_BUCKET_NAME, FILTER_MODE_BUCKET_NAME].includes(this.props.title)
        ) {
            return (
                <div className={styles['drawer-section']}>
                    <h4 className={styles['section-title']}>{this.props.title}</h4>
                    {bucketSection}
                </div>
            );
        }

        return <div className={styles['drawer-section']}>{bucketSection}</div>;
    }

    handleShowSecondaryFilters = () => {
        this.props.onShowAllClick();
    };
}
